import {
  Building2,
  Calendar,
  ClipboardCheck,
  CreditCard,
  FileText,
  Home,
  Link,
  MapPin,
  MessageSquare,
  MessagesSquare,
  NotebookPen,
  Pencil,
  PenLine,
  Search,
  Settings,
  Shield,
  ThumbsDown,
  ThumbsUp,
  UserRoundCheck,
  Users,
} from "lucide-react";
import {
  ENTERPRISE_REGISTRATION_TABS,
  REGISTRATION_BASE_HREF,
  registrationTabHref,
} from "@/lib/routes/registration-paths";
import {
  EVENT_MESSAGES_PATH,
  ORG_EVENTS_PATH,
  eventDetailsHref,
  eventHomeHref,
  eventNotesHref,
} from "@/lib/routes/enterprise-nav-paths";
import { ManageTab, OrganizationSubsection, OrganizationTab, SidebarMode, SourceTab } from "./enums";
import { NavGroup, NavItem, NavigationState } from "./types";

/** Label suffix appended to a `comingSoon` item for non-admins. */
const COMING_SOON_LABEL_SUFFIX = " (coming soon)";
/** Tooltip shown on a `comingSoon` item for non-admins. */
const COMING_SOON_TOOLTIP = "Coming soon";

/**
 * Standalone Events nav item — the org-wide Events list, promoted out of the "Organization"
 * group to the top of the nav (it's the default post-login landing page; see
 * `resolveLandingDestination`). Org-scoped, not tied to an event id.
 */
export const eventsNavItem: NavItem = {
  id: OrganizationSubsection.EVENTS,
  label: "Events",
  icon: Calendar,
  href: ORG_EVENTS_PATH,
};

/**
 * Standalone Home nav item rendered above the "SOURCE" group header.
 * Points at `/dashboard/home/{eventId}` where the planner-facing Home page lives.
 */
export const homeNavItem: NavItem = {
  id: "home",
  label: "Home",
  icon: Home,
  href: "/dashboard/home/{eventId}",
};

/**
 * Standalone Event Details nav item rendered between Home and Notes. Points at the per-event
 * details form at `/dashboard/details/{eventId}` (attendees, budget, decision date, documents).
 *
 * Labelled as a verb with a pencil because clients revising dates or rooms did not read
 * "Details" with an info icon as the place to make changes.
 */
export const eventDetailsNavItem: NavItem = {
  id: "details",
  label: "Edit event details",
  icon: Pencil,
  href: "/dashboard/details/{eventId}",
};

/**
 * Standalone Users nav item rendered below Event Details. Not its own page — jumps straight to
 * Event Details' existing "Users" section (`SECTION_IDS.USERS`) via URL hash, the same anchor its
 * own side nav links to.
 */
export const eventUsersNavItem: NavItem = {
  id: "users",
  label: "Users",
  icon: Users,
  href: "/dashboard/details/{eventId}#users",
};

/**
 * Standalone Notes nav item rendered below Home. Points at the event-wide
 * shared notes feed at `/dashboard/notes/{eventId}`.
 */
export const notesNavItem: NavItem = {
  id: "notes",
  label: "Notes",
  icon: NotebookPen,
  href: "/dashboard/notes/{eventId}",
};

/**
 * Standalone Messages nav item rendered in the Event group, below Notes. Points at the
 * event's conversations with the hotels it has an RFP out to.
 *
 * Its id stays a {@link SourceTab} because the route still lives under `/dashboard/source/`,
 * so `parsePathname` reports it as a source tab. Only the group it renders in moved; see
 * {@link eventGroupActiveId}, which is what reconciles the two.
 *
 * `MessagesSquare`, not `MessageSquare`: Chat holds the single bubble, and both items sit in
 * the same rail at once.
 */
export const messagesNavItem: NavItem = {
  id: SourceTab.MESSAGES,
  label: "Messages",
  icon: MessagesSquare,
  href: EVENT_MESSAGES_PATH.replace("[event_id]", "{eventId}"),
};

/**
 * Standalone Chat nav item rendered directly below Home. Org-scoped — not
 * tied to an event id. Points at the ZED-backed AI assistant.
 */
export const chatNavItem: NavItem = {
  id: "chat",
  label: "Chat",
  icon: MessageSquare,
  href: "/dashboard/chat",
};

/**
 * Source navigation group configuration (event-specific).
 * Note: hrefs use placeholder {eventId} which gets replaced at runtime.
 */
export const sourceGroup: NavGroup = {
  id: "source",
  title: "Source",
  items: [
    {
      id: SourceTab.SEARCH,
      label: "Search",
      icon: Search,
      href: "/dashboard/source/{eventId}/search/hotel",
    },
    {
      id: SourceTab.PROPOSALS,
      label: "Proposals",
      icon: FileText,
      href: "/dashboard/source/{eventId}/proposals",
    },
    {
      id: SourceTab.CONTRACTS,
      label: "Contracts",
      icon: PenLine,
      href: "/dashboard/source/{eventId}/contracts",
    },
    {
      id: SourceTab.PAYMENTS,
      label: "Payments",
      icon: CreditCard,
      href: "/dashboard/source/{eventId}/payments",
      comingSoon: true,
    },
  ],
};

// `REGISTRATION_BASE_HREF`/`registrationTabHref` now live in `@/lib/routes/registration-paths` —
// the single shared source for the Registration URL scheme (see that module's doc comment).
export { registrationTabHref as registrationHref };

/**
 * Registration's drill-down children, sourced from the same config the page itself reads
 * (`ENTERPRISE_REGISTRATION_TABS`) so the sidebar and the page can't drift. The demo "overview" tab
 * is flag-gated (see `REGISTRATION_FALLBACK_TAB`) — `showOverview` should mirror the
 * `enterprise-registration-overview-tab` flag the page itself reads, so the sidebar only offers a
 * tab the page will actually render. Communications' own destinations (All templates / Communication
 * settings / Scheduled sends) are a `FetchTabBar` inside that tab's content, not a second sidebar
 * drill-down level — so, unlike Settings' children, none of these have `children`.
 *
 * @param showOverview - Whether the demo Overview tab is enabled for the current user.
 * @returns Registration's drill-down nav items.
 */
export function buildRegistrationDrillDownChildren(showOverview: boolean): NavItem[] {
  return ENTERPRISE_REGISTRATION_TABS.filter((tab) => showOverview || tab.id !== "overview").map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: tab.icon,
    href: registrationTabHref(tab.id),
  }));
}

/**
 * Whether Registration is open to every user, or still gated to Nowadays admins.
 *
 * `manageGroup`'s Registration item carries `comingSoon: true`, and this is the override the
 * sidebar hands `buildNavGroupForUser` to open it back up (matching `canAccessEventRegistration`,
 * which is `Number.isFinite(eventId)` today). It lives here rather than inline in the sidebar
 * because two other surfaces hand out the same link: the command palette lists Registration's
 * tabs, and the AI chat's link map is derived from that list. Tightening this into a
 * real entitlement check has to reach all three at once, or chat keeps naming pages that bounce
 * the user to Home.
 *
 * @returns True while Registration is generally available.
 */
export const canSeeRegistration = (): boolean => true;

/**
 * Manage navigation group configuration (event-specific). Registration is a drill-down item, same
 * mechanism Settings uses — but its children swap client-side (see `useRegistrationNavStore`)
 * instead of navigating, since a real route change would remount the page and refetch every tab.
 */
export const manageGroup: NavGroup = {
  id: "manage",
  title: "Manage",
  items: [
    {
      id: ManageTab.REGISTRATION,
      label: "Registration",
      icon: UserRoundCheck,
      href: REGISTRATION_BASE_HREF,
      comingSoon: true,
      drillDown: true,
      children: buildRegistrationDrillDownChildren(false),
    },
  ],
};

/**
 * Derive a navigation group's per-user presentation.
 *
 * Items flagged `comingSoon` are gated — shown disabled with a "(coming soon)" label suffix and
 * the "Coming soon" tooltip — unless the user is allowed. Allowance is decided per item: if
 * `accessOverrides` has an explicit entry for the item id, that wins (the item is accessible only
 * when its override is `true`); otherwise the item falls back to the Nowadays-admin default. This
 * lets the Registration item be driven purely by its own access gate (flag / assets) — even for
 * admins — while items without an override (e.g. Payments) stay admin-only. Items without the
 * `comingSoon` flag are returned untouched. Pure function — safe to call at render time; the
 * static group config is never mutated.
 *
 * @param group - The base navigation group from this module.
 * @param isAdmin - Whether the current user is a Nowadays admin (the default for un-overridden items).
 * @param accessOverrides - Optional per-item-id map; an explicit entry overrides the admin default.
 * @returns A new group with `comingSoon` items presented per the user's access.
 */
export const buildNavGroupForUser = (
  group: NavGroup,
  isAdmin: boolean,
  accessOverrides?: Partial<Record<string, boolean>>
): NavGroup => ({
  ...group,
  items: group.items.map((item) => {
    if (!item.comingSoon) return item;
    const hasOverride = accessOverrides != null && Object.prototype.hasOwnProperty.call(accessOverrides, item.id);
    const accessible = hasOverride ? accessOverrides[item.id] === true : isAdmin;
    return accessible
      ? item
      : {
          ...item,
          label: `${item.label}${COMING_SOON_LABEL_SUFFIX}`,
          disabled: true,
          disabledTooltip: COMING_SOON_TOOLTIP,
        };
  }),
});

/**
 * Organization navigation items configuration.
 * "Settings" triggers a drill-down view in the sidebar.
 * Other items are flat clickable items.
 *
 * Events lives outside this array — see {@link eventsNavItem} — promoted to a standalone
 * top-level item instead of one of many items under "Organization".
 */
export const organizationItems: NavItem[] = [
  {
    id: OrganizationSubsection.SETTINGS,
    label: "Settings",
    icon: Settings,
    href: "/dashboard/organization/settings/profile",
    drillDown: true,
    children: [
      {
        id: OrganizationTab.PROFILE,
        label: "Profile",
        icon: Building2,
        href: "/dashboard/organization/settings/profile",
      },
      {
        id: OrganizationTab.OFFICE_LOCATION,
        label: "Office locations",
        icon: MapPin,
        href: "/dashboard/organization/settings/office-location",
      },
      {
        id: OrganizationTab.PREFERRED_VENUES,
        label: "Preferred venues",
        icon: ThumbsUp,
        href: "/dashboard/organization/settings/preferred-venues",
      },
      {
        id: OrganizationTab.BLACKLISTED_VENUES,
        label: "Blacklisted venues",
        icon: ThumbsDown,
        href: "/dashboard/organization/settings/blacklisted-venues",
      },
      {
        id: OrganizationTab.POLICIES,
        label: "Policies",
        icon: Shield,
        href: "/dashboard/organization/settings/policies",
      },
      {
        id: OrganizationTab.CONTRACTS,
        label: "Contracts",
        icon: FileText,
        href: "/dashboard/organization/settings/contracts",
      },
      {
        id: OrganizationTab.INTEGRATIONS,
        label: "Integrations",
        icon: Link,
        href: "/dashboard/organization/settings/integrations",
      },
    ],
  },
  {
    id: OrganizationSubsection.USERS,
    label: "Users",
    icon: Users,
    href: "/dashboard/organization/users",
  },
  {
    id: OrganizationSubsection.APPROVALS,
    label: "Approvals",
    icon: ClipboardCheck,
    href: "/dashboard/organization/approvals",
  },
  {
    id: OrganizationSubsection.REPORTING,
    label: "Reporting",
    icon: FileText,
    href: "/dashboard/organization/reporting",
  },
];

/**
 * Parse current pathname to extract navigation state.
 *
 * Expected URL patterns:
 * - /dashboard/home/[event_id]
 * - /dashboard/notes/[event_id]
 * - /dashboard/details/[event_id]
 * - /dashboard/source/[event_id]/[tab]
 * - /dashboard/source/[event_id]/[tab]/[childTab]
 * - /dashboard/manage/[event_id]/[tab]
 * - /dashboard/manage/[event_id]/[tab]/[childTab]
 * - /dashboard/organization/[subsection]
 * - /dashboard/organization/[subsection]/[tab]
 * - /dashboard/chat
 * - /dashboard/explore/* (not in sidebar)
 */
export const parsePathname = (pathname: string): NavigationState => {
  const parts = pathname.split("/").filter(Boolean);
  const empty: NavigationState = { section: null, eventId: null, tab: null, childTab: null };

  if (parts[0] !== "dashboard") {
    return empty;
  }

  const sectionValue = parts[1];

  if (
    sectionValue === "home" ||
    sectionValue === "notes" ||
    sectionValue === "source" ||
    sectionValue === "manage" ||
    sectionValue === "details"
  ) {
    // /dashboard/source/[event_id]/[tab]/[childTab]
    // /dashboard/manage/[event_id]/[tab]/[childTab]
    return { section: sectionValue, eventId: parts[2] || null, tab: parts[3] || null, childTab: parts[4] || null };
  }

  if (sectionValue === "organization") {
    // /dashboard/organization/[subsection]/[tab]
    const tab = parts[2] || null;
    const childTab = parts[3] || null;
    return { section: "organization", eventId: null, tab, childTab };
  }

  if (sectionValue === "chat") {
    return { section: "chat", eventId: null, tab: null, childTab: null };
  }

  // /dashboard/explore/* or unknown — not tracked in sidebar
  return empty;
};

/**
 * Maps a {@link NavigationState.section} to the sidebar mode that owns it. Deliberately partial:
 * `chat` (visible unchanged in both modes) and unknown/null sections have no entry, so the
 * mode-reconciliation effect in `useSidebarPanel` leaves the current mode alone for them instead
 * of forcing a swap — see that hook's doc for why mode is click-driven, not path-derived, there.
 */
export const SECTION_TO_MODE: Partial<Record<NonNullable<NavigationState["section"]>, SidebarMode>> = {
  home: "event",
  details: "event",
  notes: "event",
  source: "event",
  manage: "event",
  organization: "org",
};

/**
 * Pure, total sidebar-mode resolution for a pathname — used only for the sidebar's initial mount
 * value (there's no "previous mode" to preserve yet). Defaults to `"event"` for any path with no
 * entry in {@link SECTION_TO_MODE} (`chat`, unknown routes) — an arbitrary but harmless choice
 * for a cold load; `useSidebarPanel`'s corrective effect does NOT use this default, so it never
 * fights the "stay wherever you were" behavior chat needs after the first render.
 *
 * @param pathname - `window.location.pathname` / Next.js `usePathname()`.
 * @returns The sidebar mode this path belongs to.
 */
export function deriveSidebarMode(pathname: string): SidebarMode {
  const { section } = parsePathname(pathname);
  return (section && SECTION_TO_MODE[section]) || "event";
}

/**
 * Replace {eventId} placeholder in href with actual event ID.
 */
export const replaceEventIdInHref = (href: string, eventId: string): string => {
  return href.replace("{eventId}", eventId);
};

/**
 * Shared by {@link shouldNavigateOnOrgItemClick} and {@link shouldNavigateOnManageItemClick}: a
 * drill-down item skips navigation when the click happened while already on its section/tab
 * (preserves the current sub-tab / avoids remounting the page).
 */
function shouldNavigateOnDrillDownRootClick(
  item: NavItem,
  navState: NavigationState,
  section: NavigationState["section"],
  tab: string
): boolean {
  if (!item.drillDown) return true;
  return !(navState.section === section && navState.tab === tab);
}

export const shouldNavigateOnOrgItemClick = (item: NavItem, navState: NavigationState): boolean =>
  shouldNavigateOnDrillDownRootClick(item, navState, "organization", OrganizationSubsection.SETTINGS);

/**
 * Same idea as {@link shouldNavigateOnOrgItemClick} for the Manage group: re-clicking "Registration"
 * while already on a registration page should just (re)open the drill-down, not re-navigate —
 * a real navigation would remount the page and refetch every tab.
 */
export const shouldNavigateOnManageItemClick = (item: NavItem, navState: NavigationState): boolean =>
  shouldNavigateOnDrillDownRootClick(item, navState, "manage", ManageTab.REGISTRATION);

/** Sections whose sub-tab should carry over when switching events via {@link resolveEventSwitchHref};
 *  anything else falls back to the new event's Home. */
const TAB_PRESERVING_SECTIONS = new Set<NavigationState["section"]>(["source", "manage"]);

/**
 * Whether a section's `childTab` is safe to carry over to a different event. Source's childTabs
 * (a contract id, a package id, `compare`) are always scoped to a record in the *current* event —
 * carrying one over points at a record that doesn't exist (or, worse, at the wrong record) under
 * the new event, so Source always drops it. Manage's only childTab is Registration's active
 * sub-tab, a tab identifier rather than a record id, so it's safe to keep — but only when it's a
 * real registration tab, not a stale/unrecognized value.
 *
 * @param section - The current section.
 * @param childTab - The current childTab, or null.
 * @returns Whether `childTab` should be appended to the destination href.
 */
function isChildTabSafeToCarryOver(section: NavigationState["section"], childTab: string | null): boolean {
  if (!childTab) return true;
  if (section !== "manage") return false;
  return ENTERPRISE_REGISTRATION_TABS.some((t) => t.id === childTab);
}

/**
 * Resolve the destination href for switching to `newEventId` from the given nav state — shared by
 * every event-switch UI (the sidebar's merged `EventSwitchControl`, the breadcrumb's
 * `EventBreadcrumbSwitcher`) so they can't drift on where a switch lands. Preserves the Details
 * and Notes routes (both tabless, so there's no sub-tab to validate) and Source/Manage's active
 * sub-tab (dropping a childTab that wouldn't resolve under the new event — see
 * {@link isChildTabSafeToCarryOver}); everything else lands on the new event's Home.
 *
 * @param navState - Parsed pathname of the page the switch is happening from.
 * @param newEventId - The event id being switched to.
 * @returns The href to navigate to.
 */
export function resolveEventSwitchHref(navState: NavigationState, newEventId: string): string {
  if (navState.section === "details") {
    return eventDetailsHref(Number(newEventId));
  }

  if (navState.section === "notes") {
    return eventNotesHref(Number(newEventId));
  }

  if (navState.section && TAB_PRESERVING_SECTIONS.has(navState.section) && navState.tab) {
    const childSuffix =
      navState.childTab && isChildTabSafeToCarryOver(navState.section, navState.childTab)
        ? `/${navState.childTab}`
        : "";
    return `/dashboard/${navState.section}/${newEventId}/${navState.tab}${childSuffix}`;
  }

  return eventHomeHref(Number(newEventId));
}

/**
 * The Event group's items, in display order.
 *
 * Exported from here rather than declared in the component that renders them,
 * because the sidebar is not the only reader: the command palette derives its
 * rows from the same config, and so does the AI chat's link map. While this
 * list lived in `EventModeNav` those two could not see it, and the one item
 * that moved into this group stopped being searchable without anything saying
 * so.
 */
export const eventGroupItems: NavItem[] = [eventDetailsNavItem, notesNavItem, messagesNavItem, eventUsersNavItem];

/** What {@link eventGroupActiveId} needs from the parsed, optimistic nav state. */
interface EventGroupActiveInput {
  /** The Event Details page is showing. */
  details: boolean;
  /** The Notes feed is showing. */
  notes: boolean;
  /** Which source tab the pathname parses to, if any. */
  sourceTab: string | null;
}

/**
 * @description Which Event-group item to highlight, or null for none.
 *
 *   Messages is the awkward one: it renders in this group but its route sits under
 *   `/dashboard/source/`, so it arrives as a source tab rather than as its own flag. Reading it
 *   here keeps that seam in one place instead of spreading a special case through the sidebar.
 *
 *   Users is deliberately absent. It targets a hash section of the Details page, and nothing
 *   tracks the hash, so highlighting it would be a guess.
 * @param {EventGroupActiveInput} active - The parsed nav state this group cares about.
 * @returns {string | null} The item id to mark active, or null.
 */
export function eventGroupActiveId({ details, notes, sourceTab }: EventGroupActiveInput): string | null {
  if (details) return eventDetailsNavItem.id;
  if (notes) return notesNavItem.id;
  if (sourceTab === SourceTab.MESSAGES) return messagesNavItem.id;
  return null;
}
